// Admin Payouts workspace (plan §11). Route-backed Finance → Payouts view, separate from
// deposits/purchases (CashTab). Prize payouts only.
//
// Review and release are distinct actions. Release is the money-moving action: it mints a
// server-verified, single-use, actor+action-bound step-up proof (no manual code) and only
// enqueues a worker job server-side (never a synchronous bank call). All actions use
// STRUCTURED dialogs — no window.prompt. On-system colors (green/neutral/blue/red, no amber).
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Panel, PageHeader, StatGrid, StatTile, DataTable, TRow, Chip, CardHead,
  EmptyRow, Loading, BtnPrimary, BtnGhost, BtnDanger, cx,
} from "@/components/admin/kit";
import { adminPayoutsApi, fmtMoney } from "@/lib/payouts";
import { RedemptionStatusChip } from "@/components/payouts/RedemptionStatus";

const QUEUE_FILTERS = [
  ["all", "All"], ["under_review", "Under review"], ["awaiting_information", "Needs info"],
  ["on_hold", "On hold"], ["approved", "Approved"], ["queued", "Queued"],
  ["processing", "Processing"], ["paid", "Paid"], ["failed", "Failed"], ["returned", "Returned"],
];

const COLS = "28px minmax(130px,1.3fr) minmax(80px,0.9fr) minmax(90px,0.9fr) minmax(80px,0.8fr) minmax(100px,0.9fr) minmax(110px,1fr)";

function apiMsg(e) {
  const d = e?.response?.data?.detail;
  if (d && typeof d === "object") return d.message || d.code || "Request failed";
  return d || e?.message || "Request failed";
}

// Per-brand payout-method badge (matches the "12 Admin Payouts" design). Each method is tinted
// with its own brand colour on a low-opacity fill; Cash App lands on the platform green. ACH /
// unknown fall back to the neutral surface so nothing invents a colour off-system.
const METHOD_TINT = {
  cashapp: { fg: "#00D632", bg: "rgba(0,214,50,0.12)" },
  venmo: { fg: "#5AADDF", bg: "rgba(61,149,206,0.16)" },
  paypal: { fg: "#60A5FA", bg: "rgba(37,99,235,0.16)" },
  zelle: { fg: "#C084FC", bg: "rgba(168,85,247,0.16)" },
};
function MethodBadge({ method, label, rail, className }) {
  const key = (method || "").toLowerCase();
  const text = label || (rail === "ach" ? "ACH Bank" : rail || "—");
  const t = METHOD_TINT[key];
  const style = t
    ? { background: t.bg, color: t.fg }
    : { background: "#141417", color: "#a1a1aa", boxShadow: "inset 0 0 0 1px #26262B" };
  return (
    <span className={cx("inline-flex items-center gap-1 rounded-md px-2 py-[3px] text-[11px] font-bold tracking-[.01em]", className)}
      style={style}>
      <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full" style={{ background: t ? t.fg : "#52525b" }} />
      {text}
    </span>
  );
}

// Internal super-admin ON/OFF control plane. Live DB-backed switches (override-first, env
// fallback); changing one mints a step-up proof. Shown to super admins only.
function ControlsPanel({ onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => adminPayoutsApi.getSwitches().then(setData).catch(() => setData(null)), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  const eff = data.effective || {};
  const ov = data.overrides || {};

  const apply = async (changes) => {
    setBusy(Object.keys(changes)[0]);
    try { await adminPayoutsApi.setSwitches(changes); toast.success("Switch updated"); await load(); onChanged && onChanged(); }
    catch (e) { toast.error(apiMsg(e)); } finally { setBusy(null); }
  };

  const SWITCHES = [
    ["domain", "Payout domain", eff.domain_enabled, "Turns the entire payout system on/off."],
    ["intake", "Accept new redemptions", eff.intake_enabled, "Members can request cash-outs."],
    ["approval", "Allow approvals", eff.approval_enabled, "Operators can approve requests."],
    ["release", "Allow release", eff.release_enabled, "The money-moving step is permitted."],
    ["rail_manual", "Manual P2P rail", eff.rails?.manual, "Cash App / Venmo / PayPal / Zelle, paid by hand."],
    ["rail_ach", "ACH bank rail", eff.rails?.ach, "Automated bank transfer. Needs a linked bank."],
  ];

  return (
    <Panel className="p-4">
      <CardHead title="Payout controls"
        right={<span className="text-[11px] text-[#71717a]">Live switches · step-up required · override-first, env fallback</span>} />
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        {SWITCHES.map(([key, label, on, desc]) => {
          const overridden = key in ov;
          return (
            <div key={key} className="flex items-center justify-between rounded-[10px] border border-[#1F1F23] bg-[#0E0E11] px-3 py-2">
              <div className="min-w-0">
                <div className="text-[13px] font-semibold text-[#e4e4e7]">{label}</div>
                <div className="text-[11px] text-[#71717a]">{desc}{overridden ? " · overridden" : " · env default"}</div>
              </div>
              <div className="flex items-center gap-2">
                {overridden && (
                  <button className="text-[11px] text-[#71717a] hover:text-[#a1a1aa]" title="Revert to env default"
                    disabled={busy === key} onClick={() => apply({ [key]: null })}>reset</button>
                )}
                <button
                  disabled={busy === key}
                  onClick={() => apply({ [key]: !on })}
                  className="rounded-full px-3 py-1 text-[12px] font-bold"
                  style={{ backgroundColor: on ? "#00D632" : "#2A2A30", color: on ? "#0A0A0A" : "#a1a1aa" }}>
                  {busy === key ? "…" : on ? "ON" : "OFF"}
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-4 text-[12px] text-[#a1a1aa]">
        <label className="flex items-center gap-2">
          Active adapter
          <select value={eff.active_adapter_key || "simulator"} disabled={busy === "adapter"}
            onChange={(e) => apply({ adapter: e.target.value })}
            className="rounded-[8px] border border-[#1F1F23] bg-[#141417] px-2 py-1 text-white outline-none">
            {["simulator", "manual", "ach_bank"].map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2">
          Environment
          <select value={eff.environment || "simulator"} disabled={busy === "environment"}
            onChange={(e) => apply({ environment: e.target.value })}
            className="rounded-[8px] border border-[#1F1F23] bg-[#141417] px-2 py-1 text-white outline-none">
            {["simulator", "sandbox", "production"].map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </label>
        {eff.environment === "production" && (
          <span className="text-[11px]" style={{ color: "#EF4444" }}>production — real payouts when release + a rail are ON</span>
        )}
      </div>
    </Panel>
  );
}

// Limits & policy editor (super admin). Each save writes a NEW versioned policy (CONFIGURE +
// step-up); requests keep the version they were created under. Set conservative limits before
// opening payouts to members — a go-live prerequisite, now editable from the UI.
function LimitsPanel({ onChanged }) {
  const [p, setP] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => adminPayoutsApi.getPolicy().then((d) => {
    setP(d);
    setForm({
      min: (d.min_minor / 100).toFixed(2),
      max: (d.max_per_request_minor / 100).toFixed(2),
      cap: (d.monthly_cap_minor / 100).toFixed(2),
      fee: ((d.player_fee_minor || 0) / 100).toFixed(2),
      review: String(d.review_target_business_days ?? 2),
      version: `${d.version || "policy"}-${(d.version || "").includes("-") ? "r" : "v"}2`,
    });
  }).catch(() => setP(null)), []);
  useEffect(() => { load(); }, [load]);
  if (!p || !form) return null;

  const dollars = (s) => Math.round(parseFloat(s || "0") * 100);
  const save = async () => {
    if (!form.version.trim()) { toast.error("Enter a new version label"); return; }
    setBusy(true);
    try {
      await adminPayoutsApi.setPolicy(form.version.trim(), {
        min_minor: dollars(form.min), max_per_request_minor: dollars(form.max),
        monthly_cap_minor: dollars(form.cap), player_fee_minor: dollars(form.fee),
        review_target_business_days: parseInt(form.review || "2", 10),
      });
      toast.success("Policy updated");
      await load(); onChanged && onChanged();
    } catch (e) { toast.error(apiMsg(e)); } finally { setBusy(false); }
  };

  const F = ({ label, k, prefix }) => (
    <label className="flex flex-col gap-1 text-[12px] text-[#71717a]">
      {label}
      <div className="flex items-center gap-1">
        {prefix && <span className="text-[#a1a1aa]">{prefix}</span>}
        <Input value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} />
      </div>
    </label>
  );

  return (
    <Panel className="p-4">
      <CardHead title="Limits & policy"
        right={<span className="text-[11px] text-[#71717a]">active v{p.version} · new save = new version · step-up</span>} />
      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <F label="Minimum redemption" k="min" prefix="$" />
        <F label="Max per request" k="max" prefix="$" />
        <F label="Monthly cap" k="cap" prefix="$" />
        <F label="Player fee" k="fee" prefix="$" />
        <F label="Review target in business days" k="review" />
        <F label="New version label" k="version" />
      </div>
      <div className="mt-3">
        <BtnPrimary disabled={busy} onClick={save}>{busy ? "Saving…" : "Save policy"}</BtnPrimary>
      </div>
    </Panel>
  );
}

export default function PayoutsTab() {
  const [overview, setOverview] = useState(null);
  const [filter, setFilter] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);     // detail doc
  const [action, setAction] = useState(null);          // { type, reference }
  const [picked, setPicked] = useState({});            // ref -> true (approved rows for batching)
  const [busy, setBusy] = useState(false);
  const [batchRefresh, setBatchRefresh] = useState(0); // bump to reload the batches panel
  const [view, setView] = useState("operations");       // "operations" | "controls"

  const loadOverview = useCallback(() => {
    adminPayoutsApi.overview().then(setOverview).catch(() => setOverview(null));
  }, []);
  const loadQueue = useCallback(() => {
    setLoading(true);
    adminPayoutsApi.queue(filter === "all" ? null : filter)
      .then((d) => setRows(Array.isArray(d) ? d : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => { loadQueue(); }, [loadQueue]);

  const openDetail = (reference) =>
    adminPayoutsApi.detail(reference).then(setSelected).catch((e) => toast.error(apiMsg(e)));
  const refresh = () => {
    loadOverview(); loadQueue(); setBatchRefresh((n) => n + 1);
    if (selected) openDetail(selected.reference);
  };

  const pickedRefs = Object.keys(picked).filter((r) => picked[r]);
  const domainOff = overview?.flags && overview.flags.domain_enabled === false;
  const adapterKey = overview?.flags?.active_adapter_key || "simulator";

  const createBatchFromSelected = async () => {
    if (pickedRefs.length === 0) { toast.error("Select one or more approved requests"); return; }
    setBusy(true);
    try {
      const b = await adminPayoutsApi.createBatch({ references: pickedRefs, funding_key: adapterKey });
      toast.success(`Batch ${b.batch_id} created (${fmtMoney(b.total)})`);
      setPicked({});
    } catch (e) { toast.error(apiMsg(e)); } finally { setBusy(false); refresh(); }
  };

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Payouts"
        meta="Prize redemptions — review, approve and release. Separate from deposits & purchases."
        actions={<BtnGhost onClick={refresh}>Refresh</BtnGhost>}
      />

      {/* Operations (queue/batches) vs Controls (super-admin switches + limits) */}
      <div className="flex flex-wrap gap-2">
        <Chip active={view === "operations"} onClick={() => setView("operations")}>Operations</Chip>
        <Chip active={view === "controls"} onClick={() => setView("controls")}>Controls</Chip>
      </div>

      {view === "controls" ? (
        <div className="flex flex-col gap-4">
          <ControlsPanel onChanged={refresh} />
          <LimitsPanel onChanged={refresh} />
        </div>
      ) : (
      <div className="flex flex-col gap-4">
      {domainOff && (
        <Panel className="p-4 text-[13px]" style={{ color: "#a1a1aa" }}>
          Payout domain is OFF. This workspace is read-only and empty until a super admin turns
          the domain on in <button className="font-semibold underline" onClick={() => setView("controls")}
            style={{ color: "#00D632" }}>Controls</button>.
        </Panel>
      )}

      {overview && (
        <StatGrid cols={6}>
          <StatTile icon="transfer" label="Under review" value={overview.counts?.under_review ?? 0} />
          <StatTile icon="transfer" label="Approved" value={overview.counts?.approved ?? 0} valueColor="#00D632" />
          <StatTile icon="transfer" label="Processing" value={overview.counts?.processing ?? 0} valueColor="#60A5FA" />
          <StatTile icon="transfer" label="Paid" value={overview.counts?.paid ?? 0} valueColor="#00D632" />
          <StatTile icon="transfer" label="On hold" value={overview.counts?.on_hold ?? 0} valueColor="#d4d4d8" />
          <StatTile icon="audit" label="Exceptions" value={overview.open_exceptions ?? 0} valueColor="#EF4444" />
        </StatGrid>
      )}

      <Panel className="p-3.5">
        <CardHead
          title="Queue"
          count={rows.length}
          right={pickedRefs.length > 0 && (
            <BtnPrimary disabled={busy} onClick={createBatchFromSelected}>
              Create batch from selected ({pickedRefs.length})
            </BtnPrimary>
          )}
        />
        <div className="my-3 flex flex-wrap gap-2">
          {QUEUE_FILTERS.map(([k, label]) => (
            <Chip key={k} active={filter === k} onClick={() => setFilter(k)}>{label}</Chip>
          ))}
        </div>
        <DataTable
          cols={COLS}
          header={[{ label: "" }, { label: "Reference" }, { label: "Player" },
                   { label: "Method" }, { label: "Amount", right: true },
                   { label: "State" }, { label: "Created" }]}
        >
          {loading ? (
            <Loading />
          ) : rows.length === 0 ? (
            <EmptyRow>No requests in this view.</EmptyRow>
          ) : (
            rows.map((r) => {
              const canPick = r.state === "approved";
              return (
                <TRow key={r.reference} cols={COLS} className="hover:bg-white/[.02]">
                  <span onClick={(e) => e.stopPropagation()}>
                    {canPick && (
                      <input
                        type="checkbox"
                        aria-label={`Select ${r.reference} for batch`}
                        checked={!!picked[r.reference]}
                        onChange={(e) => setPicked((p) => ({ ...p, [r.reference]: e.target.checked }))}
                      />
                    )}
                  </span>
                  <span className="cursor-pointer font-mono text-[12px] text-[#e4e4e7]"
                    onClick={() => openDetail(r.reference)}>{r.reference}</span>
                  <span className="truncate text-[#a1a1aa]">{r.player_id}</span>
                  <span className="min-w-0"><MethodBadge method={r.method} label={r.method_label} rail={r.rail} /></span>
                  <span className="text-right font-display font-bold">{fmtMoney(r.amount)}</span>
                  <span><RedemptionStatusChip state={r.state} /></span>
                  <span className="text-[12px] text-[#71717a]">{fmtDate(r.created_at)}</span>
                </TRow>
              );
            })
          )}
        </DataTable>
      </Panel>

      <BatchesPanel adapterKey={adapterKey} refreshKey={batchRefresh} />

      {selected && (
        <DetailPanel
          detail={selected}
          onClose={() => setSelected(null)}
          onAction={(type) => setAction({ type, reference: selected.reference })}
        />
      )}

      <ActionDialog action={action} detail={selected} onClose={() => setAction(null)}
        onDone={() => { setAction(null); refresh(); }} />
      </div>
      )}
    </div>
  );
}

// Prominent operator "send payment to" card for a manual (P2P) payout. Shows the member's
// chosen app, the FULL handle (copyable), the exact net amount to send, and the member's
// verified name so the operator can name-match before paying by hand.
function PayToBlock({ detail }) {
  const d = detail.destination;
  if (!d || d.rail !== "manual") return null;
  const payee = detail.payee || {};
  const net = detail.net || detail.amount;
  const copy = (txt) => {
    try { navigator.clipboard.writeText(txt); toast.success("Copied"); } catch { /* no-op */ }
  };
  return (
    <div className="mt-4 rounded-[14px] border p-4"
      style={{ borderColor: "rgba(0,214,50,0.35)", background: "rgba(0,214,50,0.06)" }}>
      <div className="mb-2 flex items-center justify-between">
        <SectionLabel>Send payment to</SectionLabel>
        <MethodBadge method={d.method} label={d.method_label} rail={d.rail} />
      </div>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wide text-[#71717a]">{d.method_label} handle</div>
          <div className="truncate font-mono text-[18px] font-bold text-white">{d.handle || "—"}</div>
        </div>
        {d.handle && (
          <BtnGhost onClick={() => copy(d.handle)}>Copy</BtnGhost>
        )}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3 text-[13px]">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-[#71717a]">Amount to send</div>
          <div className="font-display text-[20px] font-extrabold" style={{ color: "#00D632" }}>{fmtMoney(net)}</div>
          {detail.fee && (detail.fee.minor > 0) && (
            <div className="text-[11px] text-[#71717a]">gross {fmtMoney(detail.amount)} · fee {fmtMoney(detail.fee)}</div>
          )}
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wide text-[#71717a]">Pay to (name-match)</div>
          <div className="truncate text-[14px] font-semibold text-[#e4e4e7]">{payee.legal_name || payee.username || "—"}</div>
          {payee.email && <div className="truncate text-[11px] text-[#71717a]">{payee.email}</div>}
        </div>
      </div>
      <div className="mt-2 flex items-center gap-2 text-[11px]">
        <span style={{ color: d.verified ? "#00D632" : "#EAB308" }}>●</span>
        <span className="text-[#a1a1aa]">
          {d.verified ? `Ownership reviewed (${d.verified_via || "verified"})` : "Handle NOT yet ownership-reviewed — confirm the name matches before paying"}
        </span>
      </div>
    </div>
  );
}

function DetailPanel({ detail, onClose, onAction }) {
  const st = detail.state;
  const canReview = ["under_review", "awaiting_information", "on_hold"].includes(st);
  const canApprove = ["under_review", "awaiting_information", "on_hold"].includes(st);
  const canRelease = st === "approved" || st === "queued";
  // manual (operator-disbursed) payout awaiting hand-payment: settle after paying by hand
  const canSettleManual = st === "processing";
  // a manually-connected P2P handle starts UNVERIFIED (name_match); an operator must confirm
  // ownership (→ manual_review) before the payout can be approved on the manual rail.
  const needsOwnershipReview = detail.destination?.rail === "manual"
    && !detail.destination?.verified
    && ["under_review", "awaiting_information", "on_hold"].includes(st);
  return (
    <Panel className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <div className="font-display text-[20px] font-extrabold">{fmtMoney(detail.amount)}</div>
          <div className="font-mono text-[12px] text-[#71717a]">{detail.reference}</div>
        </div>
        <div className="flex items-center gap-2">
          <RedemptionStatusChip state={st} />
          <BtnGhost onClick={onClose}>Close</BtnGhost>
        </div>
      </div>
      <PayToBlock detail={detail} />
      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <div>
          <SectionLabel>Eligibility</SectionLabel>
          <ul className="flex flex-col gap-1 text-[13px]">
            {(detail.eligibility_snapshot?.requirements || []).map((r, i) => (
              <li key={i} className="flex items-center gap-2">
                <span style={{ color: r.status === "satisfied" ? "#00D632" : r.status === "failed" ? "#EF4444" : "#a1a1aa" }}>●</span>
                <span className="text-[#a1a1aa]">{r.code}</span>
                <span className="text-[#52525b]">· {r.status}</span>
              </li>
            ))}
          </ul>
          {detail.internal_reason && (
            <div className="mt-2 text-[12px] text-[#a1a1aa]">
              Internal note: <span className="text-[#d4d4d8]">{detail.internal_reason}</span>
            </div>
          )}
        </div>
        <div>
          <SectionLabel>Timeline</SectionLabel>
          <ul className="flex flex-col gap-1 text-[12px] text-[#71717a]">
            {(detail.timeline || []).map((t, i) => (
              <li key={i}>{t.state} — {fmtDate(t.at)}{t.reason ? ` · ${t.reason}` : ""}</li>
            ))}
          </ul>
        </div>
      </div>
      {needsOwnershipReview && (
        <div className="mt-3 rounded-[10px] border px-3 py-2 text-[12px]"
          style={{ borderColor: "rgba(234,179,8,0.4)", background: "rgba(234,179,8,0.08)", color: "#EAB308" }}>
          This {detail.destination.method_label} handle hasn't been ownership-reviewed yet. Confirm the
          member's name matches the P2P account before approving.
        </div>
      )}
      <div className="mt-4 flex flex-wrap gap-2">
        {needsOwnershipReview && <BtnPrimary onClick={() => onAction("manual-confirm")}>Confirm handle ownership</BtnPrimary>}
        {canReview && <BtnGhost onClick={() => onAction("request-information")}>Request info</BtnGhost>}
        {canReview && <BtnGhost onClick={() => onAction("hold")}>Place hold</BtnGhost>}
        {canApprove && <BtnPrimary onClick={() => onAction("approve")}>Approve</BtnPrimary>}
        {canReview && <BtnDanger onClick={() => onAction("reject")}>Reject</BtnDanger>}
        {canRelease && <BtnPrimary onClick={() => onAction("release")}>Release (step-up)</BtnPrimary>}
        {canSettleManual && <BtnPrimary onClick={() => onAction("mark-paid")}>Mark paid (manual)</BtnPrimary>}
        {canSettleManual && <BtnDanger onClick={() => onAction("mark-failed")}>Mark failed</BtnDanger>}
      </div>
    </Panel>
  );
}

function ActionDialog({ action, detail, onClose, onDone }) {
  const [field, setField] = useState("");
  const [field2, setField2] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setField(""); setField2(""); }, [action]);
  if (!action) return null;
  const { type, reference } = action;
  const dest = detail?.destination;
  const payTo = detail?.payee?.legal_name || detail?.payee?.username;

  const spec = {
    "request-information": {
      title: "Request information", desc: "Ask the player for a specific requirement.",
      run: () => adminPayoutsApi.requestInformation(reference, {
        requirement: field || "information", public_message: field2 || "Additional information is needed.",
      }),
    },
    hold: {
      title: "Place hold", desc: "Document a compliance, fraud, legal, or ops hold.",
      run: () => adminPayoutsApi.hold(reference, { reason_category: field || "review", public_message: field2 || null }),
    },
    "manual-confirm": {
      title: "Confirm handle ownership",
      desc: "Confirm this P2P handle belongs to the member via name-match review. This marks the destination manual_review so the payout can be approved. Only confirm if the member's verified name matches the account.",
      run: () => adminPayoutsApi.manualConfirmDestination(dest?.id, field),
    },
    approve: {
      title: "Approve request", desc: "Eligibility is re-checked on fresh state before approval.",
      run: () => adminPayoutsApi.approve(reference, {}),
    },
    reject: {
      title: "Reject request", desc: "Reserved funds are returned to the player once. Reason is INTERNAL.",
      run: () => adminPayoutsApi.reject(reference, { reason: field || "rejected" }),
    },
    release: {
      title: "Release for payment", desc: "Mints a single-use step-up proof and enqueues a worker job (not a synchronous bank call). Releaser must differ from approver.",
      run: () => adminPayoutsApi.release(reference, {}),
    },
    "mark-paid": {
      title: "Mark paid (manual)", desc: "Confirm you paid this member by hand via Cash App, Venmo, PayPal, or Zelle. Record the app's transaction id/confirmation — it is required and stored for audit.",
      run: () => adminPayoutsApi.markPaid(reference, field.trim()),
    },
    "mark-failed": {
      title: "Mark failed", desc: "Could not disburse — for example an invalid handle. Releases the member's reserved funds once.",
      run: () => adminPayoutsApi.markFailed(reference, field || "manual_disbursement_failed"),
    },
  }[type];

  const submit = async () => {
    if (type === "mark-paid" && !field.trim()) { toast.error("Enter the P2P transaction id/confirmation."); return; }
    setBusy(true);
    try { await spec.run(); toast.success(`${spec.title} — done`); onDone(); }
    catch (e) { toast.error(apiMsg(e)); } finally { setBusy(false); }
  };

  const needsText = ["request-information", "hold", "reject", "mark-paid", "mark-failed", "manual-confirm"].includes(type);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{spec.title}</DialogTitle>
          <DialogDescription>{spec.desc}</DialogDescription>
        </DialogHeader>
        {["mark-paid", "manual-confirm"].includes(type) && dest?.rail === "manual" && (
          <div className="rounded-[10px] border p-3 text-[13px]"
            style={{ borderColor: "rgba(0,214,50,0.3)", background: "rgba(0,214,50,0.06)" }}>
            <div className="flex items-center justify-between">
              <MethodBadge method={dest.method} label={dest.method_label} rail={dest.rail} />
              {type === "mark-paid" && (
                <span className="font-display font-bold" style={{ color: "#00D632" }}>{fmtMoney(detail.net || detail.amount)}</span>
              )}
            </div>
            <div className="mt-1 font-mono text-[15px] font-bold text-white">{dest.handle}</div>
            {payTo && <div className="text-[12px] text-[#a1a1aa]">{type === "manual-confirm" ? "member: " : "to "}{payTo}</div>}
            <div className="mt-1 text-[11px] text-[#71717a]">
              {type === "manual-confirm"
                ? "Only confirm if this handle belongs to the member above via name-match."
                : `Pay this by hand in ${dest.method_label}, then record the confirmation below.`}
            </div>
          </div>
        )}
        {needsText && (
          <div className="flex flex-col gap-3">
            <Input placeholder={
                type === "reject" ? "Reason"
                : type === "hold" ? "Reason category"
                : type === "mark-paid" ? "P2P transaction id / confirmation"
                : type === "mark-failed" ? "Reason"
                : type === "manual-confirm" ? "Review note"
                : "Requirement"}
              value={field} onChange={(e) => setField(e.target.value)} />
            {["request-information", "hold"].includes(type) && (
              <Textarea placeholder="Public message shown to the player"
                value={field2} onChange={(e) => setField2(e.target.value)} />
            )}
          </div>
        )}
        <DialogFooter>
          <BtnGhost onClick={onClose}>Cancel</BtnGhost>
          <BtnPrimary onClick={submit} disabled={busy}>{busy ? "Working…" : spec.title}</BtnPrimary>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BatchesPanel({ adapterKey, refreshKey = 0 }) {
  const [funding, setFunding] = useState(null);
  const [batches, setBatches] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [reconcile, setReconcile] = useState(null); // batch_id
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    adminPayoutsApi.funding(adapterKey).then(setFunding).catch(() => setFunding(null));
    adminPayoutsApi.listBatches().then((d) => setBatches(Array.isArray(d) ? d : [])).catch(() => {});
    adminPayoutsApi.exceptions().then((d) => setExceptions(Array.isArray(d) ? d : [])).catch(() => {});
  }, [adapterKey]);
  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <Panel className="p-3.5">
      <CardHead title="Batches, funding & exceptions" />
      <div className="my-3 flex flex-wrap items-center gap-3 text-[13px]">
        <span className="text-[#71717a]">Funding ({adapterKey}):</span>
        <b className="text-[#e4e4e7]">
          {funding ? (funding.available_minor != null ? fmtMoney({ minor: funding.available_minor }) : String(funding.available)) : "…"}
        </b>
      </div>

      <DataTable
        cols="minmax(160px,1.4fr) minmax(90px,0.8fr) minmax(70px,0.5fr) minmax(110px,1fr) minmax(200px,1.4fr)"
        header={[{ label: "Batch" }, { label: "Total", right: true }, { label: "Rows" },
                 { label: "Status" }, { label: "Actions" }]}
      >
        {batches.length === 0 ? (
          <EmptyRow>No batches yet. Select approved requests above to create one.</EmptyRow>
        ) : (
          batches.map((b) => (
            <TRow key={b.batch_id} cols="minmax(160px,1.4fr) minmax(90px,0.8fr) minmax(70px,0.5fr) minmax(110px,1fr) minmax(200px,1.4fr)">
              <span className="font-mono text-[11px] text-[#a1a1aa]">{b.batch_id}</span>
              <span className="text-right font-display font-bold">{fmtMoney(b.total)}</span>
              <span>{b.members}</span>
              <span className="text-[#a1a1aa]">{b.status}</span>
              <span className="flex gap-2">
                {b.status === "frozen" && (
                  <BtnPrimary disabled={busy} onClick={async () => {
                    setBusy(true);
                    try { await adminPayoutsApi.releaseBatch(b.batch_id); toast.success("Batch released"); }
                    catch (e) { toast.error(apiMsg(e)); } finally { setBusy(false); load(); }
                  }}>Release</BtnPrimary>
                )}
                {["released", "reconciling", "reconciled"].includes(b.status) && (
                  <BtnGhost onClick={() => setReconcile(b.batch_id)}>Reconcile</BtnGhost>
                )}
              </span>
            </TRow>
          ))
        )}
      </DataTable>

      <div className="mt-4">
        <SectionLabel>Exceptions</SectionLabel>
        {exceptions.length === 0 ? (
          <div className="text-[13px] text-[#52525b]">No open exceptions.</div>
        ) : (
          <ul className="flex flex-col gap-2">
            {exceptions.map((x) => (
              <li key={x.instruction_key} className="flex items-center justify-between rounded-[12px] border border-[#1F1F23] bg-[#0E0E11] p-2.5 text-[12px]">
                <span className="font-mono text-[#a1a1aa]">{x.instruction_key}</span>
                <span className="text-[#71717a]">{x.bank_state || x.state}</span>
                <BtnGhost onClick={async () => {
                  try { const r = await adminPayoutsApi.resolveException(x.instruction_key); toast.success(`Resolved: ${r.resolved}`); }
                  catch (e) { toast.error(apiMsg(e)); } load();
                }}>Resolve via lookup</BtnGhost>
              </li>
            ))}
          </ul>
        )}
      </div>

      <ReconcileDialog batchId={reconcile} onClose={() => setReconcile(null)}
        onDone={() => { setReconcile(null); load(); }} />
    </Panel>
  );
}

function ReconcileDialog({ batchId, onClose, onDone }) {
  const [detail, setDetail] = useState(null);
  const [outcomes, setOutcomes] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!batchId) { setDetail(null); setOutcomes({}); return; }
    adminPayoutsApi.batchDetail(batchId).then(setDetail).catch(() => setDetail(null));
  }, [batchId]);
  if (!batchId) return null;

  const setOutcome = (ref, val) => setOutcomes((o) => ({ ...o, [ref]: val }));

  const submit = async () => {
    setBusy(true);
    try {
      const r = await adminPayoutsApi.reconcileBatch(batchId, outcomes);
      toast.success(`Reconciled: ${JSON.stringify(r.reconciled_outcomes || {})}`);
      onDone();
    } catch (e) { toast.error(apiMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reconcile batch</DialogTitle>
          <DialogDescription>
            Set the bank outcome per row. Unknown never refunds funding; a contradiction
            becomes an exception; replaying is safe (idempotent).
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2 text-[13px]">
          {detail == null ? (
            <div className="text-[#71717a]">Loading…</div>
          ) : (detail.members || []).length === 0 ? (
            <div className="text-[#71717a]">No members.</div>
          ) : (
            detail.members.map((m) => (
              <div key={m.reference} className="flex items-center justify-between gap-2 rounded-[10px] border border-[#1F1F23] bg-[#0E0E11] p-2">
                <span className="font-mono text-[12px] text-[#a1a1aa]">{m.reference}</span>
                <span className="font-display text-[13px] font-bold">{fmtMoney(m.amount)}</span>
                <div className="flex gap-1">
                  {["paid", "failed", "returned"].map((v) => (
                    <button key={v}
                      onClick={() => setOutcome(m.reference, v)}
                      className="rounded-full px-2.5 py-1 text-[11px] font-semibold"
                      style={outcomes[m.reference] === v
                        ? { backgroundColor: v === "paid" ? "rgba(0,214,50,0.16)" : v === "failed" ? "rgba(239,68,68,0.15)" : "#26262B",
                            color: v === "paid" ? "#00D632" : v === "failed" ? "#EF4444" : "#d4d4d8" }
                        : { backgroundColor: "#141417", color: "#71717a" }}>
                      {v}
                    </button>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
        <DialogFooter>
          <BtnGhost onClick={onClose}>Cancel</BtnGhost>
          <BtnPrimary onClick={submit} disabled={busy || Object.keys(outcomes).length === 0}>
            {busy ? "Working…" : "Apply outcomes"}
          </BtnPrimary>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SectionLabel({ children }) {
  return <div className="mb-2 text-[10px] font-semibold uppercase tracking-[.14em] text-[#71717a]">{children}</div>;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}
